// ════════════════════════════════════════════════════════════════════
// 🜂 roleArts.js — THE EXILE WAVE, PERFORMED
// ════════════════════════════════════════════════════════════════════
// caP'n, 2026-09-13, asked whether the wave was meant to be playable at
// release or rulebook-ahead-of-board: **"1) yes <3"**.
//
// So this file is the missing half of R111–R121. Until it existed, every
// rule in that wave had its constants in `rules.js`, its validator in
// `RulesEnforcer.js` and its chapter in `RULEZ_V2.md` — and **not one
// call site anywhere**. A player read Chapter 12 or Chapter 22 and went
// looking for something the Shatterfield did not offer.
//
// ── WHY IT IS ITS OWN MODULE ────────────────────────────────────────
// The same reason `tomeResolver.js` was lifted out of the renderer: an
// Art must resolve IDENTICALLY whoever performs it, and Terror has two
// performers (the player's board and the brain). One implementation that
// both call is the only shape in which they cannot drift — and drift
// between two resolvers is the single most expensive class of bug this
// codebase has paid for (see TERROR_REFEREE §4, and the six brain/player
// asymmetries closed on 2026-09-08).
//
// It is React-free and JSX-free on purpose: `tools/harness/build.mjs`
// mirrors it into the headless engine, and a `<div>` in here fails that
// build loudly, by name.
//
// ── THE CONTRACT ────────────────────────────────────────────────────
// Every function here takes `ctx`, the same shape `tomeResolver` takes:
//
//   { code, playerIdx, oppIdx, match, matchRef, me, opponent,
//     updatePlayer, showToast, setTargetPicker, getMatch }
//
// and every one of them:
//   1. asks the GATE first (never the validator directly — the gate is
//      what carries R108's phase door and the fail-closed wrapper),
//   2. charges the fare through the caller's own till, and
//   3. stamps its cadence in the SAME write as its effect wherever it
//      can, so a firing that is observed is a firing that is paid for.
//
// ⚠ NOTHING HERE INVENTS A RULE. Every number, every cadence and every
// scope is read out of `rules.js`, which quotes caP'n verbatim above
// each one. If a number appears in this file as a literal, that is a bug.
// ════════════════════════════════════════════════════════════════════

import {
  // R115–R119 — the rifts, their roles and their prices
  RIFTS, ROLES, roleOfRift, riftNameOf, roleFare, roleCooldown,
  BODY_RIFT, SOL_RIFT, HEART_RIFT, MIND_RIFT,
  TANK_LYT_REDUCTION, TANK_TARGETS_PER_TURN,
  TERROR_ART_OVERKILL,
  WARDEN_OPTIONS, wardenOption, WARDEN_ARTS_PER_TURN,
  WARDEN_BOUNTY, WARDEN_BOUNTY_CHOICES,
  // R111 / R113 — Exiliation and the Ritual
  EXILIATION_OPTIONS, exiliationOption, exileFare, exiliationCooldown,
  exileRetrievalFare, exileRetrievalCooldown, canReachExileRift,
  EXILE_RIFT_FIELD, EXILE_RETURNED_FIELD,
  // the shared economy
  canPayVoluntary, farePoolsFor,
  MAX_KHAO_ABS, STARTING_KHAO, STARTING_ANIMA,
} from "./rules";
import { ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS } from "./cards";
import { baseId } from "./necroSeal";
// ⚠ THE TWO ANIMA LEDGER BUILDERS LIVE IN prophecyEngine, NOT IN rules.
// R100's "self-dimmed Anima feeds no Prophecy" is a PROPHECY law and its
// bookkeeping belongs with the Prophecies. Importing them from the wrong
// module is how a fare stops being recorded as self-dimmed and quietly
// starts feeding an adversary's win condition.
import { buildAnimaGainUpdate, buildSelfDimUpdate } from "./prophecyEngine";
import {
  gateRoleArt, gateExiliation, gateExileRetrieval,
  // ⚠ R32's SEAL LIVES IN THE GATE, not in rules. Every Anima gain in
  // the engine goes through it — the Warden bounty is not an exception
  // and must not become one.
  sealAnimaGain,
} from "./ruleGate";
// ⚠ AND IT LIVES UP HERE BECAUSE CRA WILL NOT BUILD IT ANYWHERE ELSE.
// This import sat BELOW the `getCard` block until 2026-09-14, which is an
// `import/first` violation — and in create-react-app an eslint ERROR is a
// FATAL compile error, not a warning. So `npm start` served nothing but
// "Compiled with problems: src\roleArts.js Line 81:1 Import in body of
// module; reorder to top" from the day the Exile Wave shipped, and the
// whole app was unreachable in the browser while every headless suite
// stayed green — because node does not care where an import sits and
// `tools/parsecheck.mjs` asks the TypeScript parser for SYNTAX only.
// ⭐ THE LESSON, WRITTEN DOWN: a green suite is not a build. Anything that
// only the dev server can catch needs the dev server. Keep every import in
// this file above the first `const`.
// ⚠ `getCard` IS BUILT HERE, NOT IMPORTED FROM cards.js, and it is the
// THIRD copy of these four lines in the engine (Shatterfield has one and
// tomeResolver has one). cards.js exports the eight ARRAYS and no lookup,
// and a lookup that does not call `baseId` cannot find a sealed card —
// `beb_a~0~2` is the id a ruined-and-resurrected being carries. Copied
// rather than imported for the same reason tomeResolver copied it: the
// two resolvers must not disagree about what a card id means, and the
// safest way to guarantee that is for both to run the same four lines.
const ALL_CARDS_FLAT = [...ESCENDANTS, ...TOMES, ...IMPERA, ...UHLTIS, ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS];
const getCard = (id) => {
  if (!id) return null;
  return ALL_CARDS_FLAT.find((c) => c.id === baseId(id)) || null;
};

// ── THE STAMP FIELDS, NAMED ONCE ────────────────────────────────────
// Every one of these lives on the SEAT (not on the rift and not on the
// being), because every cadence in this wave is per-Aenarchist: caP'n
// gave the Arts to the RIFTS and the clocks to the PLAYER. A being that
// walks out of the Mind rift does not take the Warden's spent Turn with
// it, and a being that walks in does not get a fresh one.
export const ROLE_ART_USED   = "roleArtUsed";      // { [riftIdx]: halfTurn }
export const WARDEN_ARTS_KEY = "wardenArtsThisTurn";
export const EXILIATION_KEY  = "exiliationLastTurn";
export const RITUAL_KEY      = "ritualLastTurn";
// R118's dulled swing and R116's armed overkill are TURN-LOCAL marks the
// combat code reads. They are stamped with the half-turn they were
// bought on and are worthless on any other, which is what makes them
// "that one specific Lyt Attack" rather than a debuff.
export const TANK_DULL_KEY   = "tankDull";         // { slotIdx, turn }
export const TERROR_ARM_KEY  = "terrorArmed";      // halfTurn

const _turn = (ctx) => Math.floor(Number(ctx?.matchRef?.current?.turn ?? ctx?.match?.turn ?? 0)) || 0;
const _seats = (ctx) => Number(ctx?.matchRef?.current?.seatCount ?? ctx?.match?.seatCount ?? 2) || 2;
const _live = (ctx, idx) =>
  ctx?.matchRef?.current?.players?.[idx] ?? ctx?.match?.players?.[idx] ?? null;

/** The seat's four rifts, as the validators want them. */
export const riftsOf = (seat) => (seat?.field?.esclings) || [];

/**
 * R115 — when did this seat last use THIS rift's Art? `null` means never,
 * which is what the validators read as "no cooldown to check".
 */
export const roleArtLastUsed = (seat, riftIdx) => {
  const m = seat?.[ROLE_ART_USED];
  if (!m) return null;
  const v = m[riftIdx] ?? m[String(riftIdx)];
  return v == null ? null : Math.floor(Number(v)) || 0;
};

/** R115 — the stamp to write. Returns a NEW map; never mutates. */
export const stampRoleArt = (seat, riftIdx, turn) => ({
  ...(seat?.[ROLE_ART_USED] || {}),
  [riftIdx]: Math.floor(Number(turn)) || 0,
});

/**
 * R119 — how many Warden Arts this seat has spent THIS Turn.
 *
 * ⚠ STORED AS `{ turn, n }` RATHER THAN AS A BARE COUNT, and the reason
 * is the same one `tankDull` carries its turn: a bare counter has to be
 * reset by somebody, and "somebody resets it at the hand-off" is a fifth
 * place for the hand-off to forget something. Reading it against the
 * current half-turn means it resets itself and cannot be left standing.
 */
export const wardenArtsThisTurn = (seat, turn) => {
  const w = seat?.[WARDEN_ARTS_KEY];
  if (!w || typeof w !== "object") return 0;
  return (Math.floor(Number(w.turn)) || 0) === (Math.floor(Number(turn)) || 0)
    ? (Math.floor(Number(w.n)) || 0) : 0;
};
export const stampWardenArt = (seat, turn) => ({
  turn: Math.floor(Number(turn)) || 0,
  n: wardenArtsThisTurn(seat, turn) + 1,
});

/**
 * R118 — is this adversary being's swing dulled right now?
 * Returns the Lyt reduction (0 or TANK_LYT_REDUCTION).
 *
 * ⚠ IT IS SPENT BY BEING READ ONCE, by the caller, on the swing that
 * lands. caP'n: *"it lasts for only that one specific Lyt Attack."* This
 * predicate answers "is it armed for that being on this half-turn"; the
 * combat path clears the mark as it applies it.
 */
export const tankDullFor = (defenderSeat, attackerSlotIdx, turn) => {
  const d = defenderSeat?.[TANK_DULL_KEY];
  if (!d) return 0;
  if ((Math.floor(Number(d.turn)) || 0) !== (Math.floor(Number(turn)) || 0)) return 0;
  return Number(d.slotIdx) === Number(attackerSlotIdx) ? TANK_LYT_REDUCTION : 0;
};

/** R116 — did this seat buy the Terror Art on this half-turn? */
export const terrorArmed = (seat, turn) =>
  seat?.[TERROR_ARM_KEY] != null
  && (Math.floor(Number(seat[TERROR_ARM_KEY])) || 0) === (Math.floor(Number(turn)) || 0);

/**
 * R116 — does this swing RUIN outright instead of Fracturing?
 * The Art has to be armed AND the attacker has to be standing in the Sol
 * rift: the Art belongs to the rift, never to the being.
 */
export const terrorOverkill = (seat, attackerSlotIdx, lyt, lyf, turn) =>
  Number(attackerSlotIdx) === SOL_RIFT
  && terrorArmed(seat, turn)
  && TERROR_ART_OVERKILL(lyt, lyf);

// ════════════════════════════════════════════════════════════════════
// THE FARE, PAID ONE WAY OR THE OTHER (R107 + R96)
// ════════════════════════════════════════════════════════════════════
// Every Art in this wave is priced "N Khao or N Anima" — except the Body
// rift, whose square is 3 Khao OR 1 Anima and is caP'n's own asymmetry.
// `roleFare` carries both numbers, so this pays whichever pool can carry
// its OWN number, Khao first, and never splits.
//
// Returns { paid, pool, reason }.
async function payArtFare(ctx, fare) {
  if (!fare || (!fare.khao && !fare.anima)) return { paid: true, pool: "free" };
  const seat = _live(ctx, ctx.playerIdx) || {};
  const khao = Number(seat.khao) || 0;
  const anima = Number(seat.anima ?? STARTING_ANIMA);
  if (fare.khao > 0 && khao >= fare.khao) {
    await ctx.updatePlayer(ctx.code, ctx.playerIdx, { khao: khao - fare.khao });
    return { paid: true, pool: "khao" };
  }
  // R96 — a VOLUNTARY payment may never take you to 0. No Art is exempt;
  // the Graveshift is the only exemption in the game and this is not it.
  if (fare.anima > 0 && canPayVoluntary(anima, fare.anima)) {
    await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
      anima: anima - fare.anima,
      ...buildSelfDimUpdate(seat, fare.anima),
    });
    return { paid: true, pool: "anima" };
  }
  return {
    paid: false,
    reason: `That Art costs ${fare.khao} Khao or ${fare.anima} Anima — entirely one or entirely the other, and never down to 0 Anima`,
  };
}

// ════════════════════════════════════════════════════════════════════
// 🜁 THE FOUR ROLE ARTS (R115–R119)
// ════════════════════════════════════════════════════════════════════
/**
 * Perform the Art of the rift at `riftIdx`.
 *
 * `opts` carries what a particular Art needs and nothing else:
 *   Body   — { targetSlotIdx }   the adversary being whose swing is dulled
 *   Sol    — {}                  arms overkill + offers the free ruin
 *   Heart  — { cardId }          the being to return from the Necropolis
 *   Mind   — { optionId, payCardId }
 *
 * Returns { ok, reason } and never throws: a refusal is a result.
 */
export async function performRoleArt(riftIdx, ctx, opts = {}) {
  const i = Number(riftIdx);
  const role = roleOfRift(i);
  if (!role) return { ok: false, reason: `Rift ${riftIdx} is not one of the four` };

  const turn = _turn(ctx);
  const seats = _seats(ctx);
  const seat = _live(ctx, ctx.playerIdx) || {};
  const line = riftsOf(seat);

  // ── 1 · THE GATE, NOT THE VALIDATOR ──────────────────────────────
  // `gateRoleArt` carries R108's phase door and the fail-closed wrapper
  // on top of validateRoleArt. Calling the validator directly here would
  // be the exact shape of the bug this wave already had once: a rule with
  // a validator and no gate is a rule with no enforcement.
  const v = gateRoleArt(
    { me: seat, opponent: _live(ctx, ctx.oppIdx), turn, seatCount: seats },
    i, line,
    {
      level: Number(opts.level) || 1,
      lastUsedTurn: roleArtLastUsed(seat, i),
      wardenArtsThisTurn: wardenArtsThisTurn(seat, turn),
    }
  );
  if (!v.valid) return { ok: false, reason: v.reason };

  // ── 2 · THE FARE ─────────────────────────────────────────────────
  const fare = roleFare(role, Number(opts.level) || 1);
  const paid = await payArtFare(ctx, fare);
  if (!paid.paid) return { ok: false, reason: paid.reason };

  // ── 3 · THE EFFECT, AND THE STAMP IN THE SAME WRITE ──────────────
  // The stamp rides the effect's own update wherever the effect writes
  // to this seat, so a firing that is observed is a firing that is
  // clocked. Where an Art writes to the ADVERSARY instead, the stamp is
  // its own small write — and it goes FIRST, because the alternative is
  // an Art that lands and then fails to lock.
  const stamp = { [ROLE_ART_USED]: stampRoleArt(seat, i, turn) };

  if (i === SOL_RIFT)   return performTerrorArt(ctx, { stamp, turn });
  if (i === BODY_RIFT)  return performTankArt(ctx, { stamp, turn, targetSlotIdx: opts.targetSlotIdx });
  if (i === HEART_RIFT) return performHealerArt(ctx, { stamp, turn, cardId: opts.cardId });
  if (i === MIND_RIFT)  return performWardenArt(ctx, { stamp, turn, optionId: opts.optionId, payCardId: opts.payCardId });
  return { ok: false, reason: `No Art is built for rift ${i}` };
}

// ── R116 · THE TERROR ART (Sol) ─────────────────────────────────────
// Two limbs for one fare, and the fare has already been paid above.
//
// OVERKILL is a MARK on the seat rather than an immediate effect, because
// it modifies a swing that has not happened yet — caP'n: *"if its lyt
// attack exceeds the adversary's Lyf by at least one, ruin it instead of
// fracturing."* The combat path asks `terrorOverkill` as it resolves.
//
// THE FREE RUIN is offered by the caller (it needs a target picker, which
// is a UI), and is likewise gated on the mark. Both expire with the
// half-turn, which is what "no cooldown, the pool is the only brake"
// means in practice: buy it again next Turn, or twice this one.
async function performTerrorArt(ctx, { stamp, turn }) {
  await ctx.updatePlayer(ctx.code, ctx.playerIdx, { ...stamp, [TERROR_ARM_KEY]: turn });
  ctx.showToast?.("⚔ The Terror Art — your Sol rift ruins where it strikes this Turn.", "#f87171");
  return { ok: true, armed: "terror" };
}

// ── R118 · THE TANK ART (Body) ──────────────────────────────────────
// *"Opt: can reduce 1 being's Lyt attack's Lyt power by 1 ... it lasts
// for only that one specific Lyt Attack. It can only hit 1 of your
// Adversary's beings per turn."*
//
// ⚠ THE MARK GOES ON THE DEFENDER'S OWN SEAT (this seat), keyed by the
// ATTACKER's rift index — not on the adversary's record. A seat may only
// ever write its own node without a race, and the swing is resolved by
// the attacker reading the defender, which is the direction that already
// works everywhere else in this engine.
async function performTankArt(ctx, { stamp, turn, targetSlotIdx }) {
  const t = Number(targetSlotIdx);
  if (!Number.isInteger(t) || t < 0 || t >= RIFTS.length) {
    return { ok: false, reason: "The Tank Art needs one of the adversary's beings to dull" };
  }
  await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
    ...stamp,
    [TANK_DULL_KEY]: { slotIdx: t, turn, n: TANK_TARGETS_PER_TURN },
  });
  ctx.showToast?.(`🛡 The Tank Art — that being's next swing lands ${TANK_LYT_REDUCTION} Lyt weaker.`, "#60a5fa");
  return { ok: true, dulled: t };
}

// ── R117 · THE HEALER ART (Heart) ───────────────────────────────────
// *"Healer rift gives 1 free rez once per turn, does not incur Necropolis
// limitation (same costs)"* + *"the return tally is not spent."*
//
// So it pays the ordinary Resurrection fare for the level it returns —
// already charged above, because `roleFare(HEALER, level)` IS that
// schedule — and then slips BOTH brakes: R89's one-paid-rite-per-Turn
// clock and R85's per-copy tally. Neither is touched here, and that is
// the whole content of the Art: the two lines this function does NOT
// write are the rule.
async function performHealerArt(ctx, { stamp, turn, cardId }) {
  if (!cardId) return { ok: false, reason: "The Healer Art needs a being to return" };
  const seat = _live(ctx, ctx.playerIdx) || {};
  const card = getCard(cardId);
  if (!card) return { ok: false, reason: "That card is not in the catalogue" };
  if (card.type !== "Escendant") {
    return { ok: false, reason: "Only Escendants may be Resurrected — never a Tome, Curio, Impera, U'hlti or Affinity" };
  }
  const nec = [...(seat.necropolis || [])];
  const at = nec.indexOf(cardId);
  if (at === -1) return { ok: false, reason: "That being is not in your Necropolis" };
  nec.splice(at, 1);
  await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
    ...stamp,
    necropolis: nec,
    hand: [...(seat.hand || []), cardId],
    // ⚠ `resurrectionsThisTurn` AND the per-copy tally are DELIBERATELY
    // NOT WRITTEN. R89 and R85 are what the Healer buys past, and the
    // absence of these two keys is the entire mechanism. Anyone
    // "completing" this write by adding them has deleted the Art.
  });
  ctx.showToast?.(`✚ The Healer Art — ${card.name} returns, and the tally is untouched.`, "#4ade80");
  return { ok: true, returned: cardId };
}

// ── R119 · THE WARDEN ART (Mind) ────────────────────────────────────
// Two options, both OP2, both 5 Khao or 5 Anima, and both pay a CARD
// into the Necropolis on top of the fare — caP'n wrote the card into the
// price, so it is not a target and there is no "choose one of theirs".
//
// ⚠ ONE PER AENARCHIST PER TURN, ACROSS BOTH OPTIONS. Already refused by
// the gate above; stamped here so the next call sees it.
async function performWardenArt(ctx, { stamp, turn, optionId, payCardId }) {
  const opt = wardenOption(optionId);
  if (!opt) {
    return { ok: false, reason: `"${optionId}" is not one of the Warden's two Arts` };
  }
  const seat = _live(ctx, ctx.playerIdx) || {};
  const updates = { ...stamp, [WARDEN_ARTS_KEY]: stampWardenArt(seat, turn) };

  // THE CARD IS PART OF THE PRICE. One comes out of the hand for the
  // effect-nullify, one out of the Grimoire for the swing-nullify, and
  // it goes to the Necropolis — which means it is Resurrectable later,
  // deliberately: it was spent, not exiled.
  if (opt.pay === "hand") {
    const hand = [...(seat.hand || [])];
    const at = payCardId ? hand.indexOf(payCardId) : -1;
    if (at === -1) return { ok: false, reason: "That Warden Art sends 1 card from your hand to the Necropolis — name it" };
    hand.splice(at, 1);
    updates.hand = hand;
    updates.necropolis = [...(seat.necropolis || []), payCardId];
  } else {
    const grim = { ...(seat.grimoire || {}) };
    if (!payCardId || !(grim[payCardId] > 0)) {
      return { ok: false, reason: "That Warden Art sends 1 card from your Grimoire to the Necropolis — name one that is in it" };
    }
    if (grim[payCardId] > 1) grim[payCardId] -= 1; else delete grim[payCardId];
    updates.grimoire = grim;
    updates.necropolis = [...(seat.necropolis || []), payCardId];
  }

  // THE NULLIFY ITSELF is a standing answer, not an immediate effect:
  // the Art is bought and then spent on the next thing that qualifies.
  // Stored the same way every other standing answer in this engine is —
  // a flag with the half-turn it was armed on — so the window that reads
  // it (ResponseBridge for an effect, the combat path for a swing) can
  // tell an armed Art from a stale one.
  updates[opt.nullifies === "effect" ? "wardenNullifyEffect" : "wardenNullifySwing"] = turn;

  await ctx.updatePlayer(ctx.code, ctx.playerIdx, updates);
  ctx.showToast?.(
    opt.nullifies === "effect"
      ? "👁 The Warden Art — the next card effect against you is nullified."
      : "👁 The Warden Art — the next Lyt Attack against you is nullified.",
    "#c4b5fd");
  return { ok: true, armed: opt.nullifies };
}

/**
 * R119 — THE BOUNTY. Paid to whoever ruins a Warden, at any level.
 * caP'n widened it himself: *"let's just make it a reward for ruining any
 * level Warden, doesn't matter same reward at every level."* The ATTACKER
 * chooses which half they take.
 *
 * ⚠ THE KHAO HALF TAKES THE CARD CEILING (15), NOT THE NATURAL ONE (10).
 * Derived, not ruled, and flagged in RULE_PROVENANCE: R-Khao already
 * splits the two ceilings and a bounty is an effect.
 */
export async function payWardenBounty(ctx, choice) {
  const pick = WARDEN_BOUNTY_CHOICES.includes(choice) ? choice : WARDEN_BOUNTY_CHOICES[0];
  const seat = _live(ctx, ctx.playerIdx) || {};
  if (pick === "anima") {
    const cur = Number(seat.anima ?? STARTING_ANIMA);
    const next = sealAnimaGain(cur, WARDEN_BOUNTY.anima, "Warden bounty");
    await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
      anima: next, ...buildAnimaGainUpdate(seat, next - cur),
    });
    ctx.showToast?.(`👁 Warden bounty — +${next - cur} Anima.`, "#f0d060");
    return { ok: true, took: "anima", n: next - cur };
  }
  const cur = Number(seat.maxKhao ?? STARTING_KHAO);
  const next = Math.min(MAX_KHAO_ABS, cur + WARDEN_BOUNTY.maxKhao);
  await ctx.updatePlayer(ctx.code, ctx.playerIdx, { maxKhao: next });
  ctx.showToast?.(`👁 Warden bounty — Max Khao → ${next}.`, "#f0d060");
  return { ok: true, took: "maxKhao", n: next - cur };
}

// ════════════════════════════════════════════════════════════════════
// 🕳 R111 · EXILIATION
// ════════════════════════════════════════════════════════════════════
// A Level 3 Deviant U'hltima every Aenarchist simply knows. Not a card:
// nothing to draw, nothing to build into a Book, nothing to attach.
//
// Four options, you take exactly one, and any of them spends the same
// OP2 clock. The fare is the LEVEL OF THE CARD YOU EXILE (2 / 3 / 4),
// one pool, and it does not eat your Turn's draw.
//
// ⚠ THE EXILED CARD DOES NOT GO TO THE NECROPOLIS. It goes to the Exile
// rift, which is OUT OF THE SOLREND (R112): no Book, no Resurrection, no
// tutor, no Graveshift. That is what makes the fare worth paying and
// what makes this the most expensive draw in the game.
export async function performExiliation(ctx, { optionId, exiledCardId, foundCardId }) {
  const opt = exiliationOption(optionId);
  if (!opt) return { ok: false, reason: `"${optionId}" is not one of the four Exiliations` };
  if (!exiledCardId) return { ok: false, reason: "Name the card you are giving up" };

  const turn = _turn(ctx);
  const seats = _seats(ctx);
  const seat = _live(ctx, ctx.playerIdx) || {};
  const exiledCard = getCard(exiledCardId);
  if (!exiledCard) return { ok: false, reason: "That card is not in the catalogue" };
  const level = Number(exiledCard.level) || 1;

  const v = gateExiliation(
    { me: seat, opponent: _live(ctx, ctx.oppIdx), turn, seatCount: seats },
    optionId, level,
    { lastUsedTurn: seat[EXILIATION_KEY] ?? null }
  );
  if (!v.valid) return { ok: false, reason: v.reason };

  // ── THE EXILE ITSELF, out of whichever pile the option names ─────
  const updates = {};
  const nec = [...(seat.necropolis || [])];
  const hand = [...(seat.hand || [])];
  const grim = { ...(seat.grimoire || {}) };
  const takeFrom = (where) => {
    if (where === "necropolis") {
      const at = nec.indexOf(exiledCardId);
      if (at === -1) return false;
      nec.splice(at, 1); updates.necropolis = nec; return true;
    }
    if (where === "hand") {
      const at = hand.indexOf(exiledCardId);
      if (at === -1) return false;
      hand.splice(at, 1); updates.hand = hand; return true;
    }
    if (where === "grimoire") {
      if (!(grim[exiledCardId] > 0)) return false;
      if (grim[exiledCardId] > 1) grim[exiledCardId] -= 1; else delete grim[exiledCardId];
      updates.grimoire = grim; return true;
    }
    return false;
  };
  if (!takeFrom(opt.exileFrom)) {
    return { ok: false, reason: `That card is not in your ${opt.exileFrom}` };
  }
  updates[EXILE_RIFT_FIELD] = [...(seat[EXILE_RIFT_FIELD] || []), exiledCardId];

  // ── THE FARE. One pool, never split, never down to 0 Anima. ──────
  const fare = exileFare(level);
  const pools = farePoolsFor(fare, {
    khao: Number(seat.khao) || 0, anima: Number(seat.anima ?? STARTING_ANIMA),
  });
  if (!pools.length) {
    return { ok: false, reason: `An Exiliation of a Level ${level} card costs ${fare} — payable in Khao or in Anima, never split` };
  }
  if (pools[0] === "khao") updates.khao = (Number(seat.khao) || 0) - fare;
  else {
    const a = Number(seat.anima ?? STARTING_ANIMA);
    updates.anima = a - fare;
    Object.assign(updates, buildSelfDimUpdate(seat, fare));
  }

  // ── WHAT YOU GET BACK, if the caller has already chosen it ───────
  // The FIND is optional on this call: a UI opens a picker between the
  // exile and the find, and calling twice would charge twice. When it is
  // supplied, it rides the same write — which is what makes the trade
  // atomic and unobservable half-done.
  if (foundCardId) {
    const found = getCard(foundCardId);
    if (!found) return { ok: false, reason: "That find is not in the catalogue" };
    if (!opt.finds.includes(found.type)) {
      return { ok: false, reason: `That Exiliation finds ${opt.finds.join(" or ")} — not a ${found.type}` };
    }
    if (opt.sameLevel && (Number(found.level) || 1) !== level) {
      return { ok: false, reason: `It finds a Level ${level} card — the level of what you gave up` };
    }
    // Pull it out of wherever the option says it comes from. `books`
    // means the Grimoire for a being and the Luxmoire for an Impera,
    // which is R106 obeyed rather than restated.
    if (opt.findIn === "necropolis") {
      const at2 = (updates.necropolis || nec).indexOf(foundCardId);
      if (at2 === -1) return { ok: false, reason: "That card is not in your Necropolis" };
      const n2 = [...(updates.necropolis || nec)]; n2.splice(at2, 1); updates.necropolis = n2;
    } else {
      const fromLux = found.type === "Impera";
      const pile = fromLux ? { ...(seat.luxmoire || {}) } : { ...(updates.grimoire || grim) };
      if (!(pile[foundCardId] > 0)) {
        return { ok: false, reason: `That card is not in your ${fromLux ? "Luxmoire" : "Grimoire"}` };
      }
      if (pile[foundCardId] > 1) pile[foundCardId] -= 1; else delete pile[foundCardId];
      if (fromLux) updates.luxmoire = pile; else updates.grimoire = pile;
    }
    updates.hand = [...(updates.hand || hand), foundCardId];
  }

  updates[EXILIATION_KEY] = turn;
  await ctx.updatePlayer(ctx.code, ctx.playerIdx, updates);
  ctx.showToast?.(
    `🕳 Exiliation — ${exiledCard.name} is out of the Solrend.` +
    (foundCardId ? ` ${getCard(foundCardId)?.name} is in your hand.` : ""),
    "#a78bfa");
  return { ok: true, exiled: exiledCardId, found: foundCardId || null };
}

// ════════════════════════════════════════════════════════════════════
// 🜃 R113 · THE RITUAL PROPER
// ════════════════════════════════════════════════════════════════════
// Ten Anima to reach the rift at all, and then HALF of what you actually
// hold, rounded against you (R114). Your own cards only; once ever, per
// card; and what comes back carries THE MARK — it is ruined to the Exile
// rift rather than the Necropolis, can never be Resurrected by any
// method, and can never be retrieved again.
//
// ⚠ THE TWO NUMBERS ARE NOT THE SAME NUMBER. The floor is a door and the
// fare is a price; a seat on 40 Anima pays 20 and a seat on 11 pays 6.
export async function performRitual(ctx, { cardId }) {
  if (!cardId) return { ok: false, reason: "Name the card you are buying back" };
  const turn = _turn(ctx);
  const seats = _seats(ctx);
  const seat = _live(ctx, ctx.playerIdx) || {};
  const anima = Number(seat.anima ?? STARTING_ANIMA);
  const card = getCard(cardId);

  const v = gateExileRetrieval(
    { me: seat, opponent: _live(ctx, ctx.oppIdx), turn, seatCount: seats },
    cardId,
    {
      exileRift: seat[EXILE_RIFT_FIELD] || [],
      lastUsedTurn: seat[RITUAL_KEY] ?? null,
      exileReturned: seat[EXILE_RETURNED_FIELD] || [],
      isEscendant: card?.type === "Escendant",
    }
  );
  if (!v.valid) return { ok: false, reason: v.reason };

  const fare = exileRetrievalFare(anima);
  const rift = [...(seat[EXILE_RIFT_FIELD] || [])];
  const at = rift.indexOf(cardId);
  if (at === -1) return { ok: false, reason: "That card is not in your Exile rift" };
  rift.splice(at, 1);

  await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
    [EXILE_RIFT_FIELD]: rift,
    // ⭐ THE MARK. Written in the SAME update that hands the card back,
    // because a card that leaves the rift without its Mark recorded is a
    // card that can be bought twice — and R113's "once ever, per card" is
    // the only thing holding the Exile rift's whole economy up.
    [EXILE_RETURNED_FIELD]: [...(seat[EXILE_RETURNED_FIELD] || []), cardId],
    hand: [...(seat.hand || []), cardId],
    anima: anima - fare,
    ...buildSelfDimUpdate(seat, fare),
    [RITUAL_KEY]: turn,
  });
  ctx.showToast?.(
    `🜃 The Ritual — ${card?.name || cardId} returns, Marked. ${fare} Anima paid.`,
    "#a78bfa");
  return { ok: true, returned: cardId, fare };
}

// ── WHAT A UI NEEDS TO DRAW THE RAIL ────────────────────────────────
// One function so the 2D board, the 3D board and the brain all ask the
// same question and cannot each grow their own answer.
export function artsRailFor(seat, opponent, turn, seatCount = 2) {
  const line = riftsOf(seat);
  return RIFTS.map((r) => {
    const v = gateRoleArt(
      { me: seat, opponent, turn, seatCount },
      r.i, line,
      {
        level: 1,
        lastUsedTurn: roleArtLastUsed(seat, r.i),
        wardenArtsThisTurn: wardenArtsThisTurn(seat, turn),
      }
    );
    return {
      rift: r.i,
      name: r.name,
      role: r.role,
      element: r.element,
      fare: roleFare(r.role, 1),
      cooldown: roleCooldown(r.role, seatCount),
      legal: !!v.valid,
      reason: v.valid ? null : v.reason,
    };
  });
}
